import React from 'react'
import { Box, Stack, Typography } from '@mui/material'

type Props = {
    /** Short symbol of the primary series, when there is exactly one. */
    symbol?: string
}

// A quiet silhouette of hollow candles: the shape of the chart that isn't there.
// [bodyTop, bodyHeight, wickTop, wickBottom] in an 88px-tall viewBox, brightest at the centre.
const CANDLES: Array<[number, number, number, number]> = [
    [50, 14, 44, 70],
    [42, 12, 36, 58],
    [46, 10, 40, 62],
    [34, 16, 28, 56],
    [30, 8, 24, 44],
    [24, 14, 18, 44],
    [20, 10, 14, 36],
    [26, 12, 20, 44],
    [32, 8, 26, 46],
    [30, 14, 24, 50],
    [40, 10, 34, 56],
    [44, 12, 38, 62],
    [52, 10, 46, 68]
]
const STEP = 21
const BODY_W = 8
const CENTER = (CANDLES.length - 1) / 2

const GhostCandles = () => (
    <svg width="280" height="88" viewBox="0 0 280 88" aria-hidden="true" style={{ display: 'block' }}>
        {CANDLES.map(([top, height, wickTop, wickBottom], i) => {
            const x = 8 + i * STEP
            const opacity = Math.max(0.12, 0.55 - Math.abs(i - CENTER) * 0.07)
            return (
                <g key={i} opacity={opacity} stroke="currentColor" strokeWidth="1" fill="none">
                    <line x1={x + BODY_W / 2} y1={wickTop} x2={x + BODY_W / 2} y2={top} />
                    <rect x={x + 0.5} y={top} width={BODY_W - 1} height={height} rx="1" />
                    <line x1={x + BODY_W / 2} y1={top + height} x2={x + BODY_W / 2} y2={wickBottom} />
                </g>
            )
        })}
    </svg>
)

const ChartEmptyStateOverlay: React.FC<Props> = ({ symbol }) => (
    <Box
        sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            pointerEvents: 'none',
            color: 'text.secondary'
        }}
    >
        <Stack alignItems="center">
            <GhostCandles />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2.5 }}>
                {symbol ? `No data for ${symbol} yet.` : 'No data yet.'}
            </Typography>
            <Typography variant="caption" color="text.disabled" align="center" sx={{ mt: 0.5 }}>
                Watching for updates. Candles will appear as soon as data arrives.
            </Typography>
        </Stack>
    </Box>
)

export default ChartEmptyStateOverlay
